import type { ValidationResult } from "./types.ts";
import {
  booleanAnswer,
  clamp,
  createPuzzleTemplate,
  getRandom,
  integerBetween,
  mergeConfig,
  nearlyEqual,
  normalizeDegrees,
  numberAnswer,
  textAnswer,
  type PuzzleInstance,
  type PuzzleTemplate,
} from "./framework.ts";

const scoring = {
  accuracyWeight: 0.6,
  efficiencyWeight: 0.15,
  persistenceWeight: 0.15,
  difficultyWeight: 0.1,
  passingScore: 70,
};

const coordinateMazeDefaults = { gridSize: 8, minPathLength: 4 };
const binaryBeaconDefaults = { minValue: 16, maxValue: 255 };
const vectorCannonDefaults = {
  targetXMin: 30,
  targetXMax: 90,
  targetYMin: 20,
  targetYMax: 70,
  windXMin: -15,
  windXMax: 15,
  windYMin: -10,
  windYMax: 10,
  tolerance: 3,
};
const spawnPointBuilderDefaults = { gridSize: 12, tolerance: 0.5 };
const pathfindingTrailDefaults = { segments: 3, tolerance: 0.1 };
const questEquationDefaults = {
  coefficientMin: 2,
  coefficientMax: 9,
  solutionMin: 2,
  solutionMax: 12,
};
const turretAimDefaults = { toleranceDegrees: 2 };
const transformationMachineDefaults = { tolerance: 0.05 };
const forceArenaDefaults = { tolerance: 0.1 };
const collisionZoneDefaults = { tolerance: 0.05 };
const xpCounterOverflowDefaults = { bits: 8 };

function validation(
  success: boolean,
  score: number,
  feedback: string,
  details?: ValidationResult["details"],
): ValidationResult {
  return { success, score: clamp(Math.round(score), 0, 100), feedback, details };
}

function basicFields(...fields: PuzzleInstance["expectedAnswer"]["fields"]) {
  return { fields };
}

export const coordinateMazeTemplate = createPuzzleTemplate({
  id: "coordinate-maze",
  title: "Coordinate Maze",
  area: "coordinate_crossroads",
  skills: ["Reading coordinates", "Navigation", "Cartesian grids"],
  interaction: "Grid navigation",
  competencyIds: ["cartesian-corner-competency"],
  defaultConfig: coordinateMazeDefaults,
  generateInstance(config, context) {
    const resolved = mergeConfig(coordinateMazeDefaults, config);
    const random = getRandom(context);
    const start = {
      x: integerBetween(random, 0, resolved.gridSize - 3),
      y: integerBetween(random, 0, resolved.gridSize - 3),
    };
    const east = integerBetween(random, 2, 4);
    const north = integerBetween(random, 1, 3);
    const target = {
      x: clamp(start.x + east, 0, resolved.gridSize - 1),
      y: clamp(start.y + north, 0, resolved.gridSize - 1),
    };
    const solution = `E${target.x - start.x},N${target.y - start.y}`;
    return {
      prompt: `Navigate from (${start.x}, ${start.y}) to (${target.x}, ${target.y}). Enter moves such as E3,N2.`,
      state: { start, target, solution },
      visual: { kind: "grid", width: resolved.gridSize, height: resolved.gridSize },
      hints: [
        "Compare the target x-coordinate to the start x-coordinate.",
        "Positive y movement is north on this map.",
      ],
      expectedAnswer: basicFields({
        key: "moves",
        label: "Moves",
        type: "text",
        placeholder: "E3,N2",
      }),
      scoring,
    };
  },
  validateAnswer(_config, instance, answer) {
    const moves = textAnswer(answer, "moves").toUpperCase().replace(/\s+/g, "");
    const final = parseMoves(instance.state.start as { x: number; y: number }, moves);
    const target = instance.state.target as { x: number; y: number };
    const distance = Math.hypot(final.x - target.x, final.y - target.y);
    return validation(
      distance === 0,
      distance === 0 ? 100 : Math.max(0, 100 - distance * 25),
      distance === 0
        ? "The route lands exactly on the target coordinate."
        : `The route ends at (${final.x}, ${final.y}), not the target.`,
      { finalX: final.x, finalY: final.y, distance },
    );
  },
});

export const binaryBeaconTemplate = createPuzzleTemplate({
  id: "binary-beacon",
  title: "Binary Beacon",
  area: "quantity_quay",
  skills: ["Binary conversion", "Decimal conversion", "Hex conversion"],
  interaction: "Numeric input",
  competencyIds: ["integer-harbour-competency"],
  defaultConfig: binaryBeaconDefaults,
  generateInstance(config, context) {
    const resolved = mergeConfig(binaryBeaconDefaults, config);
    const value = integerBetween(getRandom(context), resolved.minValue, resolved.maxValue);
    return {
      prompt: `Decode beacon value ${value} into binary and hexadecimal.`,
      state: {
        value,
        solutionBinary: value.toString(2),
        solutionHex: value.toString(16).toUpperCase(),
      },
      visual: { kind: "number-system", elements: [{ label: "decimal", value }] },
      hints: ["Divide by 2 for binary remainders.", "Group binary digits into nibbles for hex."],
      expectedAnswer: basicFields(
        { key: "binary", label: "Binary", type: "text", placeholder: "101010" },
        { key: "hex", label: "Hex", type: "text", placeholder: "2A" },
      ),
      scoring,
    };
  },
  validateAnswer(_config, instance, answer) {
    const binary = textAnswer(answer, "binary").replace(/^0b/i, "");
    const hex = textAnswer(answer, "hex").replace(/^0x/i, "").toUpperCase();
    const binaryCorrect = binary === instance.state.solutionBinary;
    const hexCorrect = hex === instance.state.solutionHex;
    return validation(
      binaryCorrect && hexCorrect,
      (binaryCorrect ? 50 : 0) + (hexCorrect ? 50 : 0),
      binaryCorrect && hexCorrect
        ? "Beacon decoded in both bases."
        : "One or more base conversions needs correction.",
      { binaryCorrect, hexCorrect },
    );
  },
});

export const vectorCannonTemplate = createPuzzleTemplate({
  id: "vector-cannon",
  title: "Vector Cannon",
  area: "vector_valley",
  skills: ["Vector addition", "Magnitude", "Direction"],
  interaction: "Aiming",
  competencyIds: ["arrowhead-acres-competency"],
  defaultConfig: vectorCannonDefaults,
  generateInstance(config, context) {
    const resolved = mergeConfig(vectorCannonDefaults, config);
    const random = getRandom(context);
    const target = {
      x: integerBetween(random, resolved.targetXMin, resolved.targetXMax),
      y: integerBetween(random, resolved.targetYMin, resolved.targetYMax),
    };
    const wind = {
      x: integerBetween(random, resolved.windXMin, resolved.windXMax),
      y: integerBetween(random, resolved.windYMin, resolved.windYMax),
    };
    return {
      prompt: "Aim the cannon so shot vector plus wind lands on the target.",
      state: {
        target,
        wind,
        solutionX: target.x - wind.x,
        solutionY: target.y - wind.y,
      },
      visual: { kind: "vector", elements: [{ target }, { wind }] },
      hints: ["Subtract the wind from the target to find the launch vector."],
      expectedAnswer: basicFields(
        { key: "x", label: "Launch X", type: "number" },
        { key: "y", label: "Launch Y", type: "number" },
      ),
      scoring,
    };
  },
  validateAnswer(config, instance, answer) {
    const resolved = mergeConfig(vectorCannonDefaults, config);
    const landingX = numberAnswer(answer, "x") + Number((instance.state.wind as { x: number }).x);
    const landingY = numberAnswer(answer, "y") + Number((instance.state.wind as { y: number }).y);
    const target = instance.state.target as { x: number; y: number };
    const distance = Math.hypot(landingX - target.x, landingY - target.y);
    return validation(
      distance <= resolved.tolerance,
      100 - distance * 8,
      distance <= resolved.tolerance
        ? "The resultant vector hits the target."
        : `The resultant lands ${distance.toFixed(1)} units away.`,
      { landingX, landingY, distance },
    );
  },
});

export const spawnPointBuilderTemplate = createPuzzleTemplate({
  id: "spawn-point-builder",
  title: "Spawn Point Builder",
  area: "coordinate_crossroads",
  skills: ["Coordinate placement"],
  interaction: "Drag and place",
  competencyIds: ["grid-junction-competency"],
  defaultConfig: spawnPointBuilderDefaults,
  generateInstance(config, context) {
    const resolved = mergeConfig(spawnPointBuilderDefaults, config);
    const random = getRandom(context);
    const spawn = {
      x: integerBetween(random, 2, resolved.gridSize - 3),
      y: integerBetween(random, 2, resolved.gridSize - 3),
    };
    return {
      prompt: `Place the spawn point at the safe coordinate (${spawn.x}, ${spawn.y}).`,
      state: { spawn },
      visual: { kind: "coordinate", width: resolved.gridSize, height: resolved.gridSize },
      hints: ["Use x for horizontal placement and y for vertical placement."],
      expectedAnswer: basicFields(
        { key: "x", label: "Spawn X", type: "number" },
        { key: "y", label: "Spawn Y", type: "number" },
      ),
      scoring,
    };
  },
  validateAnswer(config, instance, answer) {
    const resolved = mergeConfig(spawnPointBuilderDefaults, config);
    const spawn = instance.state.spawn as { x: number; y: number };
    const distance = Math.hypot(numberAnswer(answer, "x") - spawn.x, numberAnswer(answer, "y") - spawn.y);
    return validation(
      distance <= resolved.tolerance,
      100 - distance * 30,
      distance <= resolved.tolerance ? "Spawn point placed safely." : "The spawn point is outside the safe tile.",
      { distance },
    );
  },
});

export const pathfindingTrailTemplate = createPuzzleTemplate({
  id: "pathfinding-trail",
  title: "Pathfinding Trail",
  area: "vector_valley",
  skills: ["Distance", "Displacement"],
  interaction: "Path analysis",
  competencyIds: ["distance-dunes-competency", "displacement-delta-competency"],
  defaultConfig: pathfindingTrailDefaults,
  generateInstance(config, context) {
    const resolved = mergeConfig(pathfindingTrailDefaults, config);
    const random = getRandom(context);
    const points = [{ x: 0, y: 0 }];
    for (let index = 0; index < resolved.segments; index += 1) {
      const last = points[points.length - 1];
      points.push({
        x: last.x + integerBetween(random, -4, 6),
        y: last.y + integerBetween(random, -4, 6),
      });
    }
    const totalDistance = points.slice(1).reduce((sum, point, index) => {
      const previous = points[index];
      return sum + Math.hypot(point.x - previous.x, point.y - previous.y);
    }, 0);
    const end = points[points.length - 1];
    return {
      prompt: "Analyze the path trail. Find total distance and final displacement.",
      state: { points, totalDistance, displacementX: end.x, displacementY: end.y },
      visual: { kind: "vector", elements: [{ points }] },
      hints: ["Distance adds each segment length.", "Displacement only compares final position to start."],
      expectedAnswer: basicFields(
        { key: "distance", label: "Total distance", type: "number", step: 0.1 },
        { key: "dx", label: "Displacement X", type: "number" },
        { key: "dy", label: "Displacement Y", type: "number" },
      ),
      scoring,
    };
  },
  validateAnswer(config, instance, answer) {
    const resolved = mergeConfig(pathfindingTrailDefaults, config);
    const distanceOk = nearlyEqual(numberAnswer(answer, "distance"), Number(instance.state.totalDistance), resolved.tolerance);
    const dxOk = nearlyEqual(numberAnswer(answer, "dx"), Number(instance.state.displacementX), resolved.tolerance);
    const dyOk = nearlyEqual(numberAnswer(answer, "dy"), Number(instance.state.displacementY), resolved.tolerance);
    return validation(
      distanceOk && dxOk && dyOk,
      (distanceOk ? 40 : 0) + (dxOk ? 30 : 0) + (dyOk ? 30 : 0),
      distanceOk && dxOk && dyOk ? "Distance and displacement are correct." : "Recheck segment lengths and final displacement.",
      { distanceOk, dxOk, dyOk },
    );
  },
});

export const questEquationTemplate = createPuzzleTemplate({
  id: "quest-equation",
  title: "Quest Equation",
  area: "algebra_academy",
  skills: ["Equation solving", "Word problems"],
  interaction: "Numeric input",
  competencyIds: ["quest-equation-competency"],
  defaultConfig: questEquationDefaults,
  generateInstance(config, context) {
    const resolved = mergeConfig(questEquationDefaults, config);
    const random = getRandom(context);
    const a = integerBetween(random, resolved.coefficientMin, resolved.coefficientMax);
    const x = integerBetween(random, resolved.solutionMin, resolved.solutionMax);
    const b = integerBetween(random, -8, 12);
    const c = a * x + b;
    return {
      prompt: `Solve for x: ${a}x ${b >= 0 ? "+" : "-"} ${Math.abs(b)} = ${c}`,
      state: { a, b, c, solution: x },
      visual: { kind: "equation", elements: [{ a, b, c }] },
      hints: ["Undo addition or subtraction first.", "Then divide by the coefficient of x."],
      expectedAnswer: basicFields({ key: "x", label: "x", type: "number" }),
      scoring,
    };
  },
  validateAnswer(_config, instance, answer) {
    const correct = nearlyEqual(numberAnswer(answer, "x"), Number(instance.state.solution), 0.001);
    return validation(correct, correct ? 100 : 0, correct ? "Equation solved." : "The value does not satisfy the equation.");
  },
});

export const turretAimTemplate = createPuzzleTemplate({
  id: "turret-aim",
  title: "Turret Aim",
  area: "trigonometry_tower",
  skills: ["Angles", "Trigonometry"],
  interaction: "Aiming",
  competencyIds: ["archers-ascent-competency"],
  defaultConfig: turretAimDefaults,
  generateInstance(_config, context) {
    const random = getRandom(context);
    const dx = integerBetween(random, 10, 60);
    const dy = integerBetween(random, 5, 45);
    const angle = normalizeDegrees((Math.atan2(dy, dx) * 180) / Math.PI);
    return {
      prompt: `Aim at a target ${dx}m east and ${dy}m north. Enter the launch angle in degrees.`,
      state: { dx, dy, angle },
      visual: { kind: "trajectory", elements: [{ dx, dy }] },
      hints: ["Use atan2(vertical, horizontal)."],
      expectedAnswer: basicFields({ key: "angle", label: "Angle", type: "number", unit: "degrees", step: 0.1 }),
      scoring,
    };
  },
  validateAnswer(config, instance, answer) {
    const resolved = mergeConfig(turretAimDefaults, config);
    const error = Math.abs(normalizeDegrees(numberAnswer(answer, "angle")) - Number(instance.state.angle));
    const wrappedError = Math.min(error, 360 - error);
    return validation(
      wrappedError <= resolved.toleranceDegrees,
      100 - wrappedError * 20,
      wrappedError <= resolved.toleranceDegrees ? "The turret bearing is locked." : `The aim is off by ${wrappedError.toFixed(1)} degrees.`,
      { angleError: wrappedError },
    );
  },
});

export const transformationMachineTemplate = createPuzzleTemplate({
  id: "transformation-machine",
  title: "Transformation Machine",
  area: "matrix_manor",
  skills: ["Translation", "Rotation", "Scale"],
  interaction: "Transform calculation",
  competencyIds: ["hall-transformations-competency"],
  defaultConfig: transformationMachineDefaults,
  generateInstance(_config, context) {
    const random = getRandom(context);
    const point = { x: integerBetween(random, -4, 4), y: integerBetween(random, -4, 4) };
    const scale = integerBetween(random, 2, 4);
    const translate = { x: integerBetween(random, -5, 5), y: integerBetween(random, -5, 5) };
    const transformed = { x: point.x * scale + translate.x, y: point.y * scale + translate.y };
    return {
      prompt: `Scale point (${point.x}, ${point.y}) by ${scale}, then translate by (${translate.x}, ${translate.y}).`,
      state: { point, scale, translate, transformed },
      visual: { kind: "transform", elements: [{ point }, { scale }, { translate }] },
      hints: ["Apply scale before translation for this machine."],
      expectedAnswer: basicFields(
        { key: "x", label: "Final X", type: "number", step: 0.1 },
        { key: "y", label: "Final Y", type: "number", step: 0.1 },
      ),
      scoring,
    };
  },
  validateAnswer(config, instance, answer) {
    const resolved = mergeConfig(transformationMachineDefaults, config);
    const transformed = instance.state.transformed as { x: number; y: number };
    const distance = Math.hypot(numberAnswer(answer, "x") - transformed.x, numberAnswer(answer, "y") - transformed.y);
    return validation(
      distance <= resolved.tolerance,
      100 - distance * 30,
      distance <= resolved.tolerance ? "The transform output is correct." : "The transformed point is incorrect.",
      { distance },
    );
  },
});

export const forceArenaTemplate = createPuzzleTemplate({
  id: "force-arena",
  title: "Force Arena",
  area: "newtonian_nexus",
  skills: ["Forces", "Acceleration", "Velocity"],
  interaction: "Force calculation",
  competencyIds: ["force-foundry-competency"],
  defaultConfig: forceArenaDefaults,
  generateInstance(_config, context) {
    const random = getRandom(context);
    const mass = integerBetween(random, 2, 12);
    const acceleration = integerBetween(random, 2, 10);
    const opposingForce = integerBetween(random, 3, 20);
    const appliedForce = mass * acceleration + opposingForce;
    return {
      prompt: `A ${mass}kg body needs ${acceleration}m/s^2 acceleration while ${opposingForce}N opposes motion. Find applied force.`,
      state: { mass, acceleration, opposingForce, appliedForce },
      visual: { kind: "physics", elements: [{ mass, acceleration, opposingForce }] },
      hints: ["Net force equals mass times acceleration.", "Applied force must overcome the opposing force."],
      expectedAnswer: basicFields({ key: "force", label: "Applied force", type: "number", unit: "N", step: 0.1 }),
      scoring,
    };
  },
  validateAnswer(config, instance, answer) {
    const resolved = mergeConfig(forceArenaDefaults, config);
    const error = Math.abs(numberAnswer(answer, "force") - Number(instance.state.appliedForce));
    return validation(
      error <= resolved.tolerance,
      100 - error * 10,
      error <= resolved.tolerance ? "The force balance is correct." : "The force does not produce the target acceleration.",
      { error },
    );
  },
});

export const collisionZoneTemplate = createPuzzleTemplate({
  id: "collision-zone",
  title: "Collision Zone",
  area: "geometry_gulch",
  skills: ["Collision primitives"],
  interaction: "Collision prediction",
  competencyIds: ["block-basin-competency"],
  defaultConfig: collisionZoneDefaults,
  generateInstance(_config, context) {
    const random = getRandom(context);
    const circle = { x: integerBetween(random, -6, -2), y: integerBetween(random, -2, 2), radius: 1 };
    const velocity = { x: integerBetween(random, 2, 5), y: 0 };
    const box = { minX: integerBetween(random, 1, 4), maxX: integerBetween(random, 5, 8), minY: -2, maxY: 2 };
    const timeToContact = (box.minX - (circle.x + circle.radius)) / velocity.x;
    const willCollide = timeToContact >= 0 && timeToContact <= 3;
    return {
      prompt: "Predict whether the moving circle collides with the box within 3 seconds. If yes, enter contact time.",
      state: { circle, velocity, box, willCollide, timeToContact },
      visual: { kind: "collision", elements: [{ circle }, { velocity }, { box }] },
      hints: ["Compare the circle's leading edge against the box minimum x."],
      expectedAnswer: basicFields(
        { key: "willCollide", label: "Will collide?", type: "boolean" },
        { key: "time", label: "Contact time", type: "number", unit: "s", step: 0.1 },
      ),
      scoring,
    };
  },
  validateAnswer(config, instance, answer) {
    const resolved = mergeConfig(collisionZoneDefaults, config);
    const collisionOk = booleanAnswer(answer, "willCollide") === Boolean(instance.state.willCollide);
    const timeOk =
      !instance.state.willCollide ||
      nearlyEqual(numberAnswer(answer, "time"), Number(instance.state.timeToContact), resolved.tolerance);
    return validation(
      collisionOk && timeOk,
      (collisionOk ? 50 : 0) + (timeOk ? 50 : 0),
      collisionOk && timeOk ? "Collision prediction is correct." : "The collision prediction needs adjustment.",
      { collisionOk, timeOk },
    );
  },
});

export const xpCounterOverflowTemplate = createPuzzleTemplate({
  id: "xp-counter-overflow",
  title: "XP Counter Overflow",
  area: "quantity_quay",
  skills: ["Integer limits", "Overflow", "Signed vs unsigned"],
  interaction: "Simulation prediction",
  competencyIds: ["integer-harbour-competency"],
  defaultConfig: xpCounterOverflowDefaults,
  generateInstance(config, context) {
    const resolved = mergeConfig(xpCounterOverflowDefaults, config);
    const maxValue = 2 ** resolved.bits - 1;
    const random = getRandom(context);
    const current = integerBetween(random, maxValue - 40, maxValue);
    const gain = integerBetween(random, 10, 80);
    const raw = current + gain;
    const finalValue = raw % (maxValue + 1);
    return {
      prompt: `An unsigned ${resolved.bits}-bit XP counter has ${current} XP and gains ${gain}. Predict overflow and final stored value.`,
      state: { bits: resolved.bits, maxValue, current, gain, wraps: raw > maxValue, finalValue },
      visual: { kind: "number-system", elements: [{ current }, { gain }, { maxValue }] },
      hints: ["Unsigned counters wrap after their maximum value."],
      expectedAnswer: basicFields(
        { key: "wraps", label: "Overflows?", type: "boolean" },
        { key: "finalValue", label: "Final stored value", type: "number" },
      ),
      scoring,
    };
  },
  validateAnswer(_config, instance, answer) {
    const wrapsOk = booleanAnswer(answer, "wraps") === Boolean(instance.state.wraps);
    const finalOk = numberAnswer(answer, "finalValue") === Number(instance.state.finalValue);
    return validation(
      wrapsOk && finalOk,
      (wrapsOk ? 40 : 0) + (finalOk ? 60 : 0),
      wrapsOk && finalOk ? "Overflow prediction matches the counter." : "Check the maximum value and wrap remainder.",
      { wrapsOk, finalOk },
    );
  },
});

export const satInspectorTemplate = createPuzzleTemplate({
  id: "sat-inspector",
  title: "SAT Inspector",
  area: "sat_summit",
  skills: ["Separating Axis Theorem"],
  interaction: "Axis inspection",
  competencyIds: ["sat-summit-competency"],
  defaultConfig: {},
  generateInstance(_config, context) {
    const random = getRandom(context);
    const a = { minX: 0, maxX: integerBetween(random, 2, 5), minY: 0, maxY: integerBetween(random, 2, 5) };
    const offsetX = integerBetween(random, 3, 8);
    const b = { minX: offsetX, maxX: offsetX + integerBetween(random, 2, 4), minY: integerBetween(random, -1, 2), maxY: integerBetween(random, 3, 6) };
    const separatedX = a.maxX < b.minX || b.maxX < a.minX;
    const separatedY = a.maxY < b.minY || b.maxY < a.minY;
    const separated = separatedX || separatedY;
    return {
      prompt: "Use SAT on the x and y axes. Are these boxes separated?",
      state: { a, b, separated, separatingAxis: separatedX ? "x" : separatedY ? "y" : "none" },
      visual: { kind: "sat", elements: [{ box: a }, { box: b }] },
      hints: ["If any axis has non-overlapping projections, the shapes are separated."],
      expectedAnswer: basicFields(
        { key: "separated", label: "Separated?", type: "boolean" },
        {
          key: "axis",
          label: "Separating axis",
          type: "choice",
          choices: [
            { id: "x", label: "x" },
            { id: "y", label: "y" },
            { id: "none", label: "none" },
          ],
        },
      ),
      scoring,
    };
  },
  validateAnswer(_config, instance, answer) {
    const separatedOk = booleanAnswer(answer, "separated") === Boolean(instance.state.separated);
    const axisOk = textAnswer(answer, "axis").toLowerCase() === String(instance.state.separatingAxis);
    return validation(
      separatedOk && axisOk,
      (separatedOk ? 50 : 0) + (axisOk ? 50 : 0),
      separatedOk && axisOk ? "SAT inspection is correct." : "Recheck projection overlap on each axis.",
      { separatedOk, axisOk },
    );
  },
});

function parseMoves(start: { x: number; y: number }, moves: string) {
  return moves.split(",").reduce(
    (position, move) => {
      const match = /^([NSEW])(\d+)$/.exec(move);
      if (!match) return position;
      const amount = Number(match[2]);
      if (match[1] === "N") return { ...position, y: position.y + amount };
      if (match[1] === "S") return { ...position, y: position.y - amount };
      if (match[1] === "E") return { ...position, x: position.x + amount };
      return { ...position, x: position.x - amount };
    },
    { ...start },
  );
}

export const specPuzzleTemplates = {
  "coordinate-maze": coordinateMazeTemplate,
  "binary-beacon": binaryBeaconTemplate,
  "vector-cannon": vectorCannonTemplate,
  "spawn-point-builder": spawnPointBuilderTemplate,
  "pathfinding-trail": pathfindingTrailTemplate,
  "quest-equation": questEquationTemplate,
  "turret-aim": turretAimTemplate,
  "transformation-machine": transformationMachineTemplate,
  "force-arena": forceArenaTemplate,
  "collision-zone": collisionZoneTemplate,
  "xp-counter-overflow": xpCounterOverflowTemplate,
  "sat-inspector": satInspectorTemplate,
} satisfies Record<string, PuzzleTemplate>;

export type SpecPuzzleTemplateId = keyof typeof specPuzzleTemplates;
